/**
 * Floral Fields Wishlist Engine
 * Handles saving favorites in localStorage, updating UI state across cards,
 * displaying toast notifications, and powering the slide-out Wishlist Drawer.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'floral_fields_wishlist';

  const Wishlist = {
    getItems: function () {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        console.error('Wishlist storage read error:', e);
        return [];
      }
    },

    saveItems: function (items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.error('Wishlist storage write error:', e);
      }
      this.syncUI();
      window.dispatchEvent(new CustomEvent('ff-wishlist:updated', { detail: { items } }));
    },

    has: function (id) {
      if (!id) return false;
      const strId = String(id);
      return this.getItems().some(item => String(item.id) === strId);
    },

    toggle: function (productData) {
      if (!productData || !productData.id) return false;
      const strId = String(productData.id);
      let items = this.getItems();
      const existingIndex = items.findIndex(item => String(item.id) === strId);

      let isAdded = false;
      if (existingIndex > -1) {
        items.splice(existingIndex, 1);
        isAdded = false;
        this.showToast(`Removed from Wishlist: ${productData.title || 'Product'}`);
      } else {
        items.unshift({
          id: strId,
          handle: productData.handle || '',
          title: productData.title || 'Dried Flowers',
          url: productData.url || `/products/${productData.handle}`,
          image: productData.image || '',
          price: productData.price || '',
          variantId: productData.variantId || '',
          variantsCount: parseInt(productData.variantsCount) || 1
        });
        isAdded = true;
        this.showToast(`Saved to Wishlist: ${productData.title || 'Product'}`);
      }

      this.saveItems(items);
      return isAdded;
    },

    remove: function (id) {
      const strId = String(id);
      let items = this.getItems().filter(item => String(item.id) !== strId);
      this.saveItems(items);
      this.renderDrawer();
    },

    clear: function () {
      this.saveItems([]);
      this.renderDrawer();
      this.showToast('Wishlist cleared');
    },

    syncUI: function () {
      const items = this.getItems();
      const count = items.length;

      // Update header badge
      document.querySelectorAll('#ff-wishlist-count, .ff-wishlist-count').forEach(el => {
        el.textContent = count;
        if (count > 0) {
          el.removeAttribute('hidden');
          el.style.display = 'inline-flex';
        } else {
          el.setAttribute('hidden', '');
          el.style.display = 'none';
        }
      });

      // Update drawer count
      const drawerCountEl = document.getElementById('ff-wishlist-drawer-count');
      if (drawerCountEl) drawerCountEl.textContent = count;

      // Update all buttons on page
      document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
        const id = btn.getAttribute('data-product-id');
        const isFavorited = this.has(id);
        btn.classList.toggle('is-active', isFavorited);
        btn.setAttribute('aria-pressed', isFavorited ? 'true' : 'false');
        btn.setAttribute('title', isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist');
        const label = btn.querySelector('.ff-pdp-wishlist-label');
        if (label) {
          label.textContent = isFavorited ? 'Saved in Wishlist' : 'Save to Wishlist';
        }
      });
    },

    showToast: function (message) {
      const toast = document.getElementById('ff-wishlist-toast');
      if (!toast) return;
      const textEl = toast.querySelector('.ff-wishlist-toast__text');
      if (textEl) textEl.textContent = message;

      toast.classList.add('is-visible');
      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 2600);
    },

    openDrawer: function () {
      const drawer = document.getElementById('ff-wishlist-drawer');
      if (!drawer) return;
      this.renderDrawer();
      drawer.removeAttribute('hidden');
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        document.body.classList.add('ff-drawer-open');
      });
    },

    closeDrawer: function () {
      const drawer = document.getElementById('ff-wishlist-drawer');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('ff-drawer-open');
      setTimeout(() => {
        drawer.setAttribute('hidden', '');
      }, 300);
    },

    renderDrawer: function () {
      const body = document.getElementById('ff-wishlist-drawer-body');
      const footer = document.getElementById('ff-wishlist-drawer-footer');
      if (!body) return;

      const items = this.getItems();
      if (items.length === 0) {
        body.innerHTML = `
          <div class="ff-wishlist-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#A89E90" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: 12px;">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <p class="ff-wishlist-empty__title">Your Wishlist is Empty</p>
            <p class="ff-wishlist-empty__desc">Save your favorite dried and preserved botanicals for easy wholesale ordering.</p>
            <a href="/collections/all" class="ff-wishlist-empty__btn" data-wishlist-close>Explore Catalog</a>
          </div>
        `;
        if (footer) footer.style.display = 'none';
        return;
      }

      if (footer) footer.style.display = 'flex';

      body.innerHTML = items.map(item => `
        <div class="ff-wishlist-item" data-wishlist-item="${item.id}">
          <a href="${item.url}" class="ff-wishlist-item__img-link">
            ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" loading="lazy">` : '<div class="ff-wishlist-item__no-img">No Image</div>'}
          </a>
          <div class="ff-wishlist-item__info">
            <h4 class="ff-wishlist-item__title">
              <a href="${item.url}">${escapeHtml(item.title)}</a>
            </h4>
            <div class="ff-wishlist-item__price">
              ${item.price ? `<span class="ff-wishlist-price-amount">${item.price}</span> <span class="ff-vat-note">ex VAT</span>` : ''}
            </div>
            <div class="ff-wishlist-item__actions">
              <button
                type="button"
                class="ff-wishlist-item__atc-btn"
                data-wishlist-atc
                data-product-handle="${item.handle}"
                data-variant-id="${item.variantId || ''}"
                data-variants-count="${item.variantsCount || 1}"
                aria-label="Add ${escapeHtml(item.title)} to basket"
              >
                <svg class="ff-atc-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                <span class="ff-atc-label">Add to Basket</span>
              </button>
              <button
                type="button"
                class="ff-wishlist-item__remove-btn"
                data-wishlist-remove="${item.id}"
                aria-label="Remove ${escapeHtml(item.title)} from Wishlist"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      `).join('');
    },

    addToCart: function (handle, variantId, variantsCount, btn) {
      if (!handle && !variantId) return;
      if (btn.classList.contains('loading')) return;

      const originalHtml = btn.innerHTML;
      btn.classList.add('loading');
      btn.disabled = true;
      btn.innerHTML = `
        <span class="ff-btn-spinner" aria-hidden="true"></span>
        <span class="ff-atc-label">Adding...</span>
      `;

      const executeAdd = (vId, title) => {
        const formData = new FormData();
        formData.append('id', vId);
        formData.append('quantity', 1);
        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript, application/json'
          },
          body: formData
        })
          .then(res => {
            if (!res.ok) throw new Error('Add to cart failed');
            return res.json();
          })
          .then(response => {
            btn.classList.remove('loading');
            btn.classList.add('success');
            btn.innerHTML = `
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span class="ff-atc-label">Added ✓</span>
            `;

            this.showToast(`Added to Basket: ${title || response.title || 'Product'}`);

            // Sync into Cart Drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function' && response.sections) {
              cartDrawer.renderContents(response);
            }

            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'ff-wishlist',
                productVariantId: vId,
                cartData: response
              });
            }

            this.refreshCart();

            setTimeout(() => {
              btn.classList.remove('success');
              btn.disabled = false;
              btn.innerHTML = originalHtml;

              // Open Dawn cart drawer and close wishlist drawer
              Wishlist.closeDrawer();
              if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              }
            }, 500);
          })
          .catch(err => {
            console.error('Cart add error:', err);
            btn.classList.remove('loading');
            btn.disabled = false;
            btn.innerHTML = originalHtml;
            this.showToast('Could not add to basket. Item may be out of stock.');
          });
      };

      if (handle) {
        fetch(`/products/${handle}.js`)
          .then(r => r.json())
          .then(product => {
            // If the product has multiple variants, open Quick View modal so user can choose option
            if (product.variants && product.variants.length > 1) {
              btn.classList.remove('loading');
              btn.disabled = false;
              btn.innerHTML = originalHtml;
              Wishlist.closeDrawer();
              if (window.FFQuickView) {
                window.FFQuickView.open(handle);
              } else {
                window.location.href = `/products/${handle}`;
              }
              return;
            }

            const availVariant = product.variants.find(v => v.available) || product.variants[0];
            if (!availVariant || !availVariant.available) {
              btn.classList.remove('loading');
              btn.disabled = true;
              btn.innerHTML = `<span class="ff-atc-label">Sold Out</span>`;
              setTimeout(() => {
                btn.disabled = false;
                btn.innerHTML = originalHtml;
              }, 2200);
              return;
            }

            executeAdd(availVariant.id, product.title);
          })
          .catch(e => {
            if (variantId) {
              executeAdd(variantId);
            } else {
              btn.classList.remove('loading');
              btn.disabled = false;
              btn.innerHTML = originalHtml;
            }
          });
      } else if (variantId) {
        executeAdd(variantId);
      }
    },

    refreshCart: function () {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.querySelectorAll('.cart-count-bubble, #cart-icon-bubble .cart-count-bubble').forEach(bubble => {
            const countSpan = bubble.querySelector('span[aria-hidden="true"]');
            if (countSpan) countSpan.textContent = cart.item_count;
            if (cart.item_count > 0) {
              bubble.removeAttribute('hidden');
              bubble.style.display = 'flex';
            }
          });
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        })
        .catch(e => console.error('Cart refresh error:', e));
    }
  };

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/[&<>'"]/g, tag => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[tag] || tag));
  }

  // Global event delegation (Capture phase to intercept before parent links)
  document.addEventListener('click', function (e) {
    // 1. Wishlist toggle on product card & PDP
    const btn = e.target.closest('[data-wishlist-btn]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const productData = {
        id: btn.getAttribute('data-product-id'),
        handle: btn.getAttribute('data-product-handle'),
        title: btn.getAttribute('data-product-title'),
        url: btn.getAttribute('data-product-url'),
        image: btn.getAttribute('data-product-image'),
        price: btn.getAttribute('data-product-price'),
        variantId: btn.getAttribute('data-product-variant-id') || '',
        variantsCount: btn.getAttribute('data-product-variants-count') || '1'
      };

      btn.classList.add('animating');
      setTimeout(() => btn.classList.remove('animating'), 350);

      Wishlist.toggle(productData);
      return;
    }

    // 2. Add to Cart from Wishlist Drawer
    const atcBtn = e.target.closest('[data-wishlist-atc]');
    if (atcBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const handle = atcBtn.getAttribute('data-product-handle');
      const variantId = atcBtn.getAttribute('data-variant-id');
      const variantsCount = parseInt(atcBtn.getAttribute('data-variants-count')) || 1;
      Wishlist.addToCart(handle, variantId, variantsCount, atcBtn);
      return;
    }

    // 3. Open drawer from header button
    const headerBtn = e.target.closest('#ff-wishlist-header-btn, [data-wishlist-open]');
    if (headerBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      Wishlist.openDrawer();
      return;
    }

    // 4. Close drawer
    if (e.target.closest('[data-wishlist-close]')) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      Wishlist.closeDrawer();
      return;
    }

    // 5. Remove item from drawer
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const id = removeBtn.getAttribute('data-wishlist-remove');
      Wishlist.remove(id);
      return;
    }

    // 6. Clear all in drawer
    if (e.target.closest('#ff-wishlist-clear-btn')) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      Wishlist.clear();
      return;
    }
  }, true);

  // Keyboard accessibility
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      Wishlist.closeDrawer();
    }
  });

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Wishlist.syncUI());
  } else {
    Wishlist.syncUI();
  }

  // Expose to window for external integrations
  window.FFWishlist = Wishlist;
})();
